import React, { useState, useRef } from 'react';
import {
  CloudUpload as Upload,
  CheckCircle,
  Cancel as XCircle,
  Loop as Loader,
  Download,
  Warning as AlertCircle,
  Description as FileText,
  Group as Users
} from '@mui/icons-material';
import axios from 'axios';

interface UploadProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  percentage: number;
}

interface UploadError {
  row: number;
  error: string;
  data?: any;
}

export const BulkRiderUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [status, setStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
  const [errors, setErrors] = useState<UploadError[]>([]);
  const [validationMode, setValidationMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setErrors([]);
      setStatus('idle');
      setProgress(null);
    }
  };

  const validateCsv = async () => {
    if (!file) return;

    setUploading(true);
    setValidationMode(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validateOnly', 'true');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/riders/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.data.isValid) {
        alert(`✅ Validation passed!\n\n${response.data.totalRecords} records are ready to upload.`);
        setErrors([]);
      } else {
        setErrors(response.data.errors || []);
        alert(
          `❌ Validation failed!\n\n${response.data.errors?.length || 0} errors found.\n\nCheck the errors list below for details.`
        );
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Validation failed';
      alert(`❌ Validation Error:\n\n${errorMsg}`);
    } finally {
      setUploading(false);
      setValidationMode(false);
    }
  };

  const startUpload = async () => {
    if (!file) return;

    if (!confirm('⚠️ This will start importing all riders from the CSV file.\n\nAre you sure you want to proceed?')) {
      return;
    }

    setUploading(true);
    setStatus('processing');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validateOnly', 'false');
    formData.append('skipExisting', 'true');
    formData.append('downloadKycDocuments', 'true');
    formData.append('batchSize', '1');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/riders/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      setUploadId(response.data.uploadId);
      // Start polling for status
      pollUploadStatus(response.data.uploadId);
    } catch (error: any) {
      console.error('Upload error:', error);
      setStatus('failed');
      const errorMsg = error.response?.data?.error || error.message || 'Upload failed to start';
      alert(`❌ Upload Error:\n\n${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const pollUploadStatus = async (id: string) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const poll = setInterval(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`/api/riders/bulk-upload/${id}/status`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        const data = response.data;

        setProgress(data.progress);
        setStatus(data.status);
        setErrors(data.errors || []);

        if (data.status === 'completed') {
          clearInterval(poll);
          pollIntervalRef.current = null;
          alert(`✅ Upload completed successfully!\n\n${data.progress.successful} riders imported successfully.\n${data.progress.failed} failed.`);
        } else if (data.status === 'failed') {
          clearInterval(poll);
          pollIntervalRef.current = null;
          alert('❌ Upload failed. Check the error log for details.');
        }
      } catch (error) {
        console.error('Status poll error:', error);
        clearInterval(poll);
        pollIntervalRef.current = null;
        setStatus('failed');
      }
    }, 2000); // Poll every 2 seconds

    pollIntervalRef.current = poll;
  };

  const downloadTemplate = () => {
    // Updated to use the correct 35-column format
    const csvContent = `Date,Timestamp,Email address,Lead Origin,Vendor Code,Freelancer Code,TC Name,FR Name,City,Client,Client Rider ID,Rider Name,Rider Mobile Num,Alternate Mobile Num,Rider DOB,Aadhar Num,PAN Num,DL Num,Bank Name,Acc Num,IFSC Code,Current Address,Nominee Name,Nominee Mobile Num,Nominee Relationship,Nominee DOB,Aadhar Card - Front,Aadhar Card - Back,PAN Card,DL,RC,Bank Passbook / Cheque,Selfie,Lead Source,Job Area Name / Location Name,RC Num,Type,Rider ID,Rider_Location,Rider gender
05-11-2024,2024-11-05 10:30:00,rider@example.com,Freelancer,,FR001,,,Bangalore,Swiggy,CLI-12345,Sample Rider,9.87654E+11,9.12345E+11,15-01-1990,1.23456789012E+11,ABCDE1234F,KA01DL1234,HDFC Bank,9.87654321012E+11,HDFC0001234,123 Sample Street Bangalore,Emergency Contact Name,9.98765E+11,Wife,20-05-1992,https://drive.google.com/file/d/YOUR_AADHAAR_FRONT_FILE_ID/view,https://drive.google.com/file/d/YOUR_AADHAAR_BACK_FILE_ID/view,https://drive.google.com/file/d/YOUR_PAN_FILE_ID/view,https://drive.google.com/file/d/YOUR_DL_FILE_ID/view,https://drive.google.com/file/d/YOUR_RC_FILE_ID/view,https://drive.google.com/file/d/YOUR_BANK_FILE_ID/view,https://drive.google.com/file/d/YOUR_SELFIE_FILE_ID/view,Social Media,Koramangala,KA01AB1234,2-Wheeler,,Koramangala,Male`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rider_bulk_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadErrors = async () => {
    if (!uploadId) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`/api/riders/bulk-upload/${uploadId}/errors`, {
        responseType: 'blob',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-upload-errors-${uploadId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading errors:', error);
      alert('Failed to download errors');
    }
  };

  const resetUpload = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setFile(null);
    setUploadId(null);
    setProgress(null);
    setStatus('idle');
    setErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Users sx={{ width: 40, height: 40 }} className="text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Bulk Rider Import</h2>
                <p className="text-blue-100 mt-1">
                  Upload multiple riders from CSV with Google Drive KYC documents
                </p>
              </div>
            </div>
          </div>

        <div className="p-6">
          {/* Template Download Section */}
          <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md">
            <div className="flex items-start gap-4">
              <div className="bg-blue-600 p-3 rounded-lg shadow-md">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-500 text-lg mb-2">Step 1: Download Template</h3>
                <p className="text-sm text-blue-800 mb-4 leading-relaxed">
                  Download the CSV template with all required columns and example data.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-md hover:shadow-lg font-medium"
                >
                  <Download sx={{ fontSize: 20 }} />
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="mb-8">
            <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              Upload Your CSV File
            </h3>
            <div className="relative border-3 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all group">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-2xl shadow-lg group-hover:scale-110 transition-transform mb-4">
                  <Upload className="w-12 h-12 text-white" />
                </div>
                <span className="text-base font-semibold text-gray-700 mb-2">
                  Click to upload CSV file
                </span>
                <span className="text-sm text-gray-500">
                  or drag and drop your file here
                </span>
              </label>
              {file && (
                <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl inline-flex items-center gap-3 shadow-sm">
                  <div className="bg-green-600 p-2 rounded-lg">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900">{file.name}</div>
                    <div className="text-xs text-gray-600">
                      {(file.size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={validateCsv}
              disabled={!file || uploading || status === 'processing'}
              className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg hover:shadow-xl font-semibold text-lg"
            >
              {validationMode && <Loader className="animate-spin" sx={{ fontSize: 22 }} />}
              {!validationMode && <AlertCircle sx={{ fontSize: 22 }} />}
              Validate Only
            </button>
            <button
              onClick={startUpload}
              disabled={!file || uploading || status === 'processing'}
              className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg hover:shadow-xl font-bold text-lg"
            >
              {uploading && <Loader className="animate-spin" sx={{ fontSize: 22 }} />}
              {!uploading && <Upload sx={{ fontSize: 22 }} />}
              Start Import
            </button>
            {(status === 'completed' || status === 'failed') && (
              <button
                onClick={resetUpload}
                className="px-6 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all shadow-lg hover:shadow-xl font-semibold"
              >
                Reset
              </button>
            )}
          </div>

          {/* Progress Section */}
          {progress && (
            <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl shadow-lg border-2 border-blue-200">
              <div className="flex justify-between items-center mb-3">
                <span className="text-base font-bold text-gray-800">
                  Import Progress
                </span>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {progress.percentage}%
                </span>
              </div>
              <div className="relative w-full bg-gray-200 rounded-full h-4 mb-6 overflow-hidden shadow-inner">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out shadow-lg"
                  style={{ width: `${progress.percentage}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-md text-center transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold text-gray-700">
                    {progress.total}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-1">Total</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold text-blue-600">
                    {progress.processed}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-1">Processed</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold text-green-600 flex items-center justify-center gap-2">
                    <CheckCircle sx={{ fontSize: 24 }} />
                    {progress.successful}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-1">Successful</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold text-red-600 flex items-center justify-center gap-2">
                    <XCircle sx={{ fontSize: 24 }} />
                    {progress.failed}
                  </div>
                  <div className="text-xs font-medium text-gray-500 mt-1">Failed</div>
                </div>
              </div>
            </div>
          )}

          {/* Status Messages */}
{status !== 'idle' && (
  <div
    className={`p-5 rounded-xl mb-8 flex items-center gap-4 shadow-lg transform transition-all
      ${
        status === 'completed'
          ? 'bg-green-50 text-green-800 border border-green-300'
          : status === 'failed'
          ? 'bg-red-50 text-red-800 border border-red-300'
          : 'bg-blue-50 text-blue-800 border border-blue-300'
      }`}
  >
    <div
      className={`p-3 rounded-full ${
        status === 'completed'
          ? 'bg-green-600'
          : status === 'failed'
          ? 'bg-red-600'
          : 'bg-blue-600'
      }`}
    >
      {status === 'processing' && (
        <Loader className="animate-spin text-white w-6 h-6" />
      )}
      {status === 'completed' && (
        <CheckCircle className="text-white w-6 h-6" />
      )}
      {status === 'failed' && (
        <XCircle className="text-white w-6 h-6" />
      )}
    </div>

    <span className="font-semibold text-lg">
      {status === 'processing' && 'Processing riders... This may take a few minutes.'}
      {status === 'completed' && '✅ Import completed successfully!'}
      {status === 'failed' && '❌ Import failed. Please check errors below.'}
    </span>
  </div>
)}

{/* Errors Section */}
{errors.length > 0 && (
  <div className="mt-8 border border-red-300 rounded-xl shadow-xl bg-white">
    {/* Header */}
    <div className="bg-red-50 p-5 flex items-center justify-between border-b border-red-200 rounded-t-xl">
      <div className="flex items-center gap-3">
        <div className="bg-red-600 p-2 rounded-lg">
          <AlertCircle className="text-white w-6 h-6" />
        </div>
        <h3 className="font-bold text-red-900 text-lg">
          Errors Found ({errors.length})
        </h3>
      </div>

      {uploadId && (
        <button
          onClick={downloadErrors}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-all"
        >
          <Download className="w-4 h-4" />
          Download CSV
        </button>
      )}
    </div>

    {/* Table */}
    <div className="max-h-96 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-100 z-10">
          <tr>
            <th className="px-5 py-3 text-left font-bold text-gray-700 w-20">
              Row
            </th>
            <th className="px-5 py-3 text-left font-bold text-gray-700">
              Error Details
            </th>
            <th className="px-5 py-3 text-left font-bold text-gray-700 w-56">
              Rider Info
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {errors.slice(0, 50).map((err, idx) => (
            <tr key={idx} className="hover:bg-red-50 transition">
              {/* Row number */}
              <td className="px-5 py-3 font-mono font-bold text-gray-900">
                <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                  {err.row}
                </span>
              </td>

              {/* Error message */}
              <td className="px-5 py-3 text-gray-800">
                <div className="font-semibold text-red-700 mb-2">
                  {err.error}
                </div>

                {err.data && (
                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border">
                    {err.data.name && (
                      <div><strong>Name:</strong> {err.data.name}</div>
                    )}
                    {err.data.phone && (
                      <div><strong>Phone:</strong> {err.data.phone}</div>
                    )}
                    {err.data.email && (
                      <div><strong>Email:</strong> {err.data.email}</div>
                    )}
                  </div>
                )}
              </td>

              {/* Rider Info */}
              <td className="px-5 py-3 text-xs text-gray-700">
                {err.data && (
                  <div className="bg-blue-50 p-2 rounded border border-blue-200 space-y-1">
                    {err.data.city && (
                      <div><strong className="text-blue-700">City:</strong> {err.data.city}</div>
                    )}
                    {err.data.clientRiderId && (
                      <div><strong className="text-blue-700">Client Rider ID:</strong> {err.data.clientRiderId}</div>
                    )}
                    {err.data.aadhaarNumber && (
                      <div><strong className="text-blue-700">Aadhaar:</strong> {err.data.aadhaarNumber}</div>
                    )}
                    {err.data.panNumber && (
                      <div><strong className="text-blue-700">PAN:</strong> {err.data.panNumber}</div>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {errors.length > 50 && (
        <div className="p-4 bg-gray-50 text-center text-sm font-medium text-gray-700 border-t">
          📊 Showing first 50 errors. Download CSV for full list.
        </div>
      )}
    </div>
  </div>
)}

{/* Instructions */}
<div className="mt-10 p-6 bg-amber-50 border border-amber-300 rounded-xl shadow-md">
  <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
    <div className="bg-amber-500 p-2 rounded-lg">
      <AlertCircle className="w-6 h-6 text-white" />
    </div>
    Important Instructions
  </h3>

  <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
    <li><strong>Export your Google Sheet as CSV format</strong></li>

    <li>
      <strong>Google Drive links must be public</strong>
      <ul className="list-disc ml-8 mt-2 text-xs space-y-1 bg-white p-3 rounded border">
        <li>Right-click → Share → "Anyone with link can view"</li>
        <li>Format: https://drive.google.com/file/d/FILE_ID/view</li>
      </ul>
    </li>

    <li><strong>Click "Validate Only" to check for errors</strong></li>
    <li><strong>Fix validation errors and re-export</strong></li>
    <li><strong>Click "Start Import" to begin</strong></li>

    <li>
      <strong>Processing time: 30–45 minutes for 13,000 riders</strong>
      <span className="text-xs ml-2">(4 documents each)</span>
    </li>

    <li className="text-red-700 font-semibold">
      ⚠️ Keep this tab open during the import
    </li>
  </ol>
</div>

        </div>
      </div>
      </div>
    </div>
  );
};